import React, { useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
import { useLoading } from "./LoadingOverlay";

/**
 * RevealOnScroll
 * - Triggers a CSS transition when the element enters the viewport.
 * - Props:
 *    variant: "fade-up" | "fade-down" | "fade-left" | "fade-right" | "zoom"
 *    duration: ms (default 700)
 *    delay: ms (default 0)
 *    threshold: 0..1 (default 0.15)
 *    once: boolean (default true) – animate only the first time it appears
 */
export default function RevealOnScroll({
  children,
  variant = "fade-up",
  duration = 1500,
  delay = 200,
  threshold = 0.45,
  once = true,
  ...boxProps
}) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  const { isLoading } = useLoading();

  useEffect(() => {
    const el = ref.current;

    if (!el) {
      return undefined;
    }

    if (isLoading) {
      setVisible(false);
      return undefined;
    }

    let observer = null;
    let firstFrameId = null;
    let secondFrameId = null;

    firstFrameId = window.requestAnimationFrame(() => {
      secondFrameId = window.requestAnimationFrame(() => {
        observer = new IntersectionObserver(
          ([entry]) => {
            if (entry.isIntersecting) {
              setVisible(true);

              if (once) {
                observer.unobserve(el);
              }
            } else if (!once) {
              setVisible(false);
            }
          },
          { threshold },
        );

        observer.observe(el);
      });
    });

    return () => {
      if (firstFrameId !== null) {
        window.cancelAnimationFrame(firstFrameId);
      }

      if (secondFrameId !== null) {
        window.cancelAnimationFrame(secondFrameId);
      }

      observer?.disconnect();
    };
  }, [isLoading, threshold, once]);

  // initial transform by variant
  const initial = {
    "fade-up":   "translate3d(0, 24px, 0) scale(1)",
    "fade-down": "translate3d(0,-24px, 0) scale(1)",
    "fade-left": "translate3d(-24px,0, 0) scale(1)",
    "fade-right":"translate3d(24px, 0, 0) scale(1)",
    "zoom":      "translate3d(0, 0, 0) scale(0.96)",
  }[variant] || "translate3d(0, 16px, 0)";

  return (
    <Box
      ref={ref}
      sx={{
        opacity: visible ? 1 : 0,
        transform: visible ? "none" : initial,
        transition: `opacity ${duration}ms ease, transform ${duration}ms cubic-bezier(.2,.65,.2,1)`,
        transitionDelay: `${delay}ms`,
        willChange: "opacity, transform",
      }}
      {...boxProps}
    >
      {children}
    </Box>
  );
}
